import React from "react";
import { observer } from "mobx-react-lite";
import { Dialog, DialogContent, DialogActions, Button, Typography, Box } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useStores } from "../hooks";
import { FaFacebook, FaLinkedin, FaXTwitter, FaThreads, FaMastodon, FaBluesky } from "react-icons/fa6";

const renderIcon = (Icon: any, className: string) => React.createElement(Icon, { className });

const YOUTUBE_VIDEO_ID = "sV_iqzo6ZoI";

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        borderRadius: 12,
    },
    videoWrapper: {
        position: "relative",
        paddingBottom: "61.8%", // 2330x1440
        height: 0,
        overflow: "hidden",
        borderRadius: 8,
        backgroundColor: "#000",
    },
    iframe: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        border: 0,
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    actions: {
        padding: theme.spacing(3, 3, 3),
        justifyContent: "center",
        gap: theme.spacing(2),
    },
    socialRow: {
        marginTop: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap" as const,
    },
    socialLabel: {
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap" as const,
        marginRight: theme.spacing(0),
    },
    socialLink: {
        display: "flex",
        alignItems: "center",
        color: "#80858F",
        opacity: 0.75,
        transition: "opacity 0.2s",
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        "&:hover": {
            opacity: 1,
        },
    },
    socialIcon: {
        width: 20,
        height: 20,
    },
    dismissBtn: {
        minWidth: 120,
    },
    startBtn: {
        minWidth: 160,
    },
}));

const WelcomeModal = observer(() => {
    const { tourStore } = useStores();
    const { showWelcomeModal, startTour, dismissWelcomeModal } = tourStore;
    const classes = useStyles();

    return (
        <Dialog open={showWelcomeModal} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialog }}>
            <DialogContent>
                <Typography variant="h6" className={classes.title}>
                    Welcome to Kibo! 👋
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    Hi, I'm{" "}
                    <a
                        href="https://ervinkalemi.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: "inherit",
                            fontWeight: 600,
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                        }}
                    >
                        Ervin
                    </a>
                    , the founder of Kibo. Watch this quick overview to get started, then take the guided tour.
                </Typography>
                <Box className={classes.videoWrapper}>
                    <iframe
                        className={classes.iframe}
                        src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&cc_load_policy=1`}
                        title="Kibo walkthrough"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </Box>
                <Box className={classes.socialRow}>
                    <Typography className={classes.socialLabel}>Follow us for updates:</Typography>
                    <a
                        href="https://facebook.com/kibohq"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                    >
                        {renderIcon(FaFacebook, classes.socialIcon)}
                    </a>
                    <a
                        href="https://www.linkedin.com/company/kibo-ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                    >
                        {renderIcon(FaLinkedin, classes.socialIcon)}
                    </a>
                    <a
                        href="https://x.com/intent/user?screen_name=kibo_ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                    >
                        {renderIcon(FaXTwitter, classes.socialIcon)}
                    </a>
                    <a
                        href="https://www.threads.com/intent/follow?username=kibo.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                    >
                        {renderIcon(FaThreads, classes.socialIcon)}
                    </a>
                    <a
                        href="https://mastodon.social/@kibo_ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                    >
                        {renderIcon(FaMastodon, classes.socialIcon)}
                    </a>
                    <a
                        href="https://bsky.app/profile/kibo.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                    >
                        {renderIcon(FaBluesky, classes.socialIcon)}
                    </a>
                </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button className={classes.dismissBtn} variant="outlined" onClick={dismissWelcomeModal}>
                    Dismiss
                </Button>
                <Button className={classes.startBtn} variant="contained" color="primary" onClick={startTour}>
                    Start the Guide
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default WelcomeModal;
